"use client"

import Link from "next/link"
import { memo, useEffect } from "react"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { GoogleIcon } from "@/components/icons/GoogleIcon"
import { useAuth } from "@/contexts/AuthContext"
import { INITIAL_FREE_PLAN_CREDITS } from "@/lib/utils"
import { AuthState } from "@/types/auth"

interface LoginDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function PureLoginDialog({ open, onOpenChange }: LoginDialogProps) {
  const { user, signInWithGoogle, loading, state } = useAuth()

  // Close the dialog the moment sign-in completes. Without this it sat
  // open on "Signing in…" until a manual page reload (AUTHENTICATED was
  // also wrongly treated as a loading state below).
  useEffect(() => {
    if (open && user) onOpenChange(false)
  }, [open, user, onOpenChange])

  const isLoading =
    loading ||
    [
      AuthState.GOOGLE_SIGNIN,
      AuthState.VERIFYING,
      AuthState.SENDING_TO_EXTENSION,
    ].includes(state)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          <DialogTitle className="text-xl">
            Sign up to fill this PDF with AI
          </DialogTitle>
          <DialogDescription>
            Get {INITIAL_FREE_PLAN_CREDITS} free starter credits to start filling
            PDFs with AI.
            Usage depends on the document and number of steps; a complex task
            may use the full allowance. Then buy credits, no subscription needed.
            Manual editing stays free.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Button
            type="button"
            variant="default"
            className="w-full"
            onClick={() => signInWithGoogle()}
            disabled={isLoading}
          >
            <GoogleIcon className="mr-3 h-5 w-5" />
            {isLoading ? "Signing in…" : "Continue with Google"}
          </Button>
          <p className="text-muted-foreground text-center text-xs leading-relaxed">
            By continuing you agree to our{" "}
            <Link
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export const LoginDialog = memo(PureLoginDialog)
